import { Link } from "react-router-dom";
import { Instagram, MessageCircle, Music2, ShieldAlert } from "lucide-react";
import Logo from "@/components/Logo";
import { useConfig } from "@/hooks/useConfig";

export function Footer() {
  const { isDemo, config } = useConfig();
  const social = config?.social || {};
  return (
    <footer className="relative z-[2] border-t border-[#1E2738] bg-[#060810]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <Logo />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
              Print Smarter. NBA picks backed by research, matchup analysis and transparent tracking.
              We don't sell guarantees. We show the work.
            </p>
            <div className="mt-5 flex items-center gap-3">
              {social.tiktok && <a href={social.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="rounded-lg border border-[#1E2738] p-2.5 text-slate-400 transition-colors hover:border-[#00E676]/50 hover:text-[#00E676]"><Music2 size={16} /></a>}
              {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="rounded-lg border border-[#1E2738] p-2.5 text-slate-400 transition-colors hover:border-[#00E676]/50 hover:text-[#00E676]"><Instagram size={16} /></a>}
              <Link to="/dashboard" aria-label="Discord (VIP members)" title="VIP Discord — access via your member dashboard" data-testid="social-discord" className="rounded-lg border border-[#1E2738] p-2.5 text-slate-400 transition-colors hover:border-[#00E676]/50 hover:text-[#00E676]"><MessageCircle size={16} /></Link>
            </div>
          </div>
          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-white">Product</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
              <li><Link to="/picks" data-testid="footer-link-picks" className="transition-colors hover:text-[#00E676]">Picks</Link></li>
              <li><Link to="/results" data-testid="footer-link-results" className="transition-colors hover:text-[#00E676]">Results</Link></li>
              <li><Link to="/how-it-works" data-testid="footer-link-how-it-works" className="transition-colors hover:text-[#00E676]">How It Works</Link></li>
              <li><Link to="/dashboard" data-testid="footer-link-dashboard" className="transition-colors hover:text-[#00E676]">Member Dashboard</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-white">Transparency</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
              <li>No false guarantees. No hidden losses.</li>
              <li>Every official pick is recorded.</li>
              {isDemo ? (
                <li data-testid="footer-demo-note">All stats currently shown are <span className="text-[#F59E0B]">demo data</span> until launch.</li>
              ) : (
                <li data-testid="footer-live-note">All stats shown are <span className="text-[#00E676]">real and fully settled</span>.</li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 rounded-xl border border-[#1E2738] bg-[#0F131D] p-5" data-testid="responsible-gambling-notice">
          <div className="flex items-start gap-3">
            <ShieldAlert size={18} className="mt-0.5 shrink-0 text-[#F59E0B]" />
            <p className="text-xs leading-relaxed text-slate-400">
              <span className="font-bold text-slate-200">18+ only. Gamble responsibly.</span> Money Printer provides
              sports analysis for informational and entertainment purposes. Nothing on this site is financial advice,
              and no outcome is ever guaranteed. Never wager more than you can afford to lose. What are you really gambling with?
              For free, confidential support 24/7 call <a href="tel:1800858858" className="font-semibold text-slate-200 underline-offset-2 hover:underline">Gambling Help Online 1800 858 858</a> or visit <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-200 underline-offset-2 hover:underline">gamblinghelponline.org.au</a>.
              To self-exclude from all Australian online wagering, register with <a href="https://www.betstop.gov.au" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-200 underline-offset-2 hover:underline">BetStop</a>.
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-3 border-t border-[#1E2738] pt-6 sm:flex-row sm:items-center">
          <p className="font-mono text-xs text-slate-500">© 2026 MONEY PRINTER. All rights reserved.</p>
          <p className="font-mono text-[11px] uppercase tracking-wider text-slate-600">
            Membership & payments securely processed by Whop · Community on Discord
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
